import { useEffect } from 'react';

interface Props {
  message: string;
  onUndo: () => void;
  onExpire: () => void;
  durationMs?: number;
}

/**
 * Bottom-center undo toast. Auto-dismisses after durationMs (default 5s).
 * Click Undo to invoke onUndo (which then closes the toast).
 *
 * Progress bar drains via a single CSS transform animation (scaleX over
 * durationMs). Previously this used setInterval(50ms) + setState which is
 * (a) banned by the design system ("setInterval-driven React animation"),
 * (b) re-rendered the parent 20 times per toast, and (c) animated `width`
 * which forces layout on every keyframe.
 */
export function UndoToast({ message, onUndo, onExpire, durationMs = 5000 }: Props) {
  useEffect(() => {
    const t = setTimeout(onExpire, durationMs);
    return () => clearTimeout(t);
  }, [durationMs, onExpire]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center"
    >
      <div className="pointer-events-auto flex items-center gap-3 overflow-hidden rounded-full border border-border bg-popover px-1 py-1 pl-4 shadow-2xl">
        <span className="text-sm text-foreground">{message}</span>
        <button
          onClick={onUndo}
          className="relative overflow-hidden rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 active:scale-[0.98]"
        >
          Undo
          <span
            className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 origin-right bg-primary-foreground/30 motion-safe:animate-[undo-drain_var(--undo-dur)_linear_forwards] motion-reduce:hidden"
            style={{ ['--undo-dur' as string]: `${durationMs}ms`, width: '100%' }}
            aria-hidden
          />
        </button>
      </div>
      <style>{`@keyframes undo-drain { from { transform: scaleX(1); } to { transform: scaleX(0); } }`}</style>
    </div>
  );
}
